// Enhanced error classes that extend the base types

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Structured error information for LLM reporting
 */
export interface StructuredError {
  type: string;
  message: string;
  severity: ErrorSeverity;
  toolId?: string;
  resourceId?: string;
  sessionId?: string;
  timestamp: number;
  details?: unknown;
  recoverable: boolean;
}

/**
 * Error recovery strategies
 */
export enum RecoveryStrategy {
  RETRY = 'retry',
  FALLBACK = 'fallback',
  SKIP = 'skip',
  ABORT = 'abort'
}

/**
 * Enhanced error base class with structured reporting
 */
export abstract class BeastMcpError extends Error {
  public readonly severity: ErrorSeverity;
  public readonly recoverable: boolean;
  public readonly recoveryStrategy: RecoveryStrategy;
  public readonly timestamp: number;
  public readonly sessionId?: string;
  public readonly toolId?: string;
  public readonly resourceId?: string;

  constructor(
    message: string,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    recoverable: boolean = true,
    recoveryStrategy: RecoveryStrategy = RecoveryStrategy.RETRY,
    sessionId?: string,
    toolId?: string,
    resourceId?: string
  ) {
    super(message);
    this.severity = severity;
    this.recoverable = recoverable;
    this.recoveryStrategy = recoveryStrategy;
    this.timestamp = Date.now();
    this.sessionId = sessionId;
    this.toolId = toolId;
    this.resourceId = resourceId;
  }

  /**
   * Convert to structured error format for LLM reporting
   */
  toStructuredError(): StructuredError {
    return {
      type: this.constructor.name,
      message: this.message,
      severity: this.severity,
      toolId: this.toolId,
      resourceId: this.resourceId,
      sessionId: this.sessionId,
      timestamp: this.timestamp,
      details: this.cause,
      recoverable: this.recoverable
    };
  }
}

/**
 * Enhanced validation error
 */
export class EnhancedValidationError extends BeastMcpError {
  constructor(
    message: string,
    public readonly field?: string,
    public readonly value?: unknown,
    sessionId?: string,
    toolId?: string
  ) {
    super(
      `Validation failed${field ? ` for field '${field}'` : ''}: ${message}`,
      ErrorSeverity.MEDIUM,
      true,
      RecoveryStrategy.RETRY,
      sessionId,
      toolId
    );
  }
}

/**
 * Enhanced tool execution error
 */
export class EnhancedToolExecutionError extends BeastMcpError {
  constructor(
    message: string,
    toolId: string,
    sessionId?: string,
    public readonly executionTime?: number
  ) {
    super(
      `Tool execution failed for '${toolId}': ${message}`,
      ErrorSeverity.HIGH,
      true,
      RecoveryStrategy.FALLBACK,
      sessionId,
      toolId
    );
  }
}

/**
 * Enhanced resource error
 */
export class EnhancedResourceError extends BeastMcpError {
  constructor(
    message: string,
    resourceId: string,
    sessionId?: string
  ) {
    super(
      `Resource access failed for '${resourceId}': ${message}`,
      ErrorSeverity.MEDIUM,
      true,
      RecoveryStrategy.SKIP,
      sessionId,
      undefined,
      resourceId
    );
  }
}

/**
 * Error handler for processing and reporting errors
 */
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: StructuredError[] = [];

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle an error and return structured information
   */
  handleError(error: Error, context?: { sessionId?: string; toolId?: string; resourceId?: string }): StructuredError {
    let structuredError: StructuredError;

    if (error instanceof BeastMcpError) {
      structuredError = error.toStructuredError();
    } else {
      structuredError = {
        type: error.constructor.name,
        message: error.message,
        severity: ErrorSeverity.HIGH,
        toolId: context?.toolId,
        resourceId: context?.resourceId,
        sessionId: context?.sessionId,
        timestamp: Date.now(),
        details: error.cause,
        recoverable: false
      };
    }

    this.errorLog.push(structuredError);
    return structuredError;
  }

  /**
   * Get error log
   */
  getErrorLog(): StructuredError[] {
    return [...this.errorLog];
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Get errors by severity
   */
  getErrorsBySeverity(severity: ErrorSeverity): StructuredError[] {
    return this.errorLog.filter(error => error.severity === severity);
  }

  /**
   * Get errors by tool/resource
   */
  getErrorsByTool(toolId: string): StructuredError[] {
    return this.errorLog.filter(error => error.toolId === toolId);
  }

  getErrorsByResource(resourceId: string): StructuredError[] {
    return this.errorLog.filter(error => error.resourceId === resourceId);
  }
} 